import clsx from 'clsx'
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { getErrorMessage } from '@features/shared/api/client'
import { IconButton } from '@features/shared/components/IconButton'
import {
  PopoverMenuDivider,
  POPOVER_MENU_ICON_SLOT_CLASS,
} from '@features/shared/components/PopoverMenu'
import { useToast } from '@features/shared/components/Toast'
import { Tooltip } from '@features/shared/components/Tooltip'
import {
  ChevronRightIcon,
  CloseIcon,
  CsvFileIcon,
  DownloadIcon,
  ExcelFileIcon,
  PdfFileIcon,
  OpenInNewIcon,
  PrinterIcon,
  SendIcon,
  SpinnerIcon,
  TrashIcon,
} from '@features/shared/icons/icons'
import type { ExportFormat, ExportJob, ExportLinkResponse } from '../api/contracts'
import { createExportBundle, getExportLink } from '../api/exports.api'
import { exportsQueryKeys } from '../api/queryKeys'
import { useCancelExport } from '../hooks/useRecentExports'
import {
  hasDraggedOut,
  keepsRecentUnfolded,
  markDraggedOut,
  rememberRecentUnfolded,
} from '../logic/dragHint'
import {
  canDragDownloads,
  canSaveAs,
  canSaveToFolder,
  downloadExport,
  downloadExports,
  downloadUrlData,
  EXPORT_BUNDLE_MIME_TYPE,
  EXPORT_FORMAT_LABELS,
  EXPORT_LINK_FRESH_MS,
  EXPORT_MIME_TYPES,
  isExportActive,
  openExport,
  openLabel,
} from '../logic/exportJobs'
import { expiresSoon, fullTimestamp, relativeAge, shortProgress } from '../logic/exportStatus'
import { canPrintInPage, printExport } from '../logic/printExport'
import { useSendExportStore } from '../stores/useSendExportStore'

/** How many of a table's or list's recent exports the menu lists. */
export const RECENT_EXPORTS_SHOWN = 10

const ACTION_CLASS =
  'rounded p-1 text-icon-quiet hover:bg-muted hover:text-data disabled:cursor-not-allowed'
// Delete is the one destructive button: red when it's pointed at.
const DELETE_CLASS =
  'rounded p-1 text-icon-quiet hover:bg-[color:var(--surface-danger-subtle)] hover:text-danger disabled:cursor-not-allowed'
// Rows line up with the Export menu's format rows: the same side padding,
// gap and 16px icon slot, so file icons sit under format icons and names
// under format names; the header's chevron sits in the same slot.
const ROW_LAYOUT_CLASS = 'flex w-full items-center gap-1.5 rounded-md px-3'
// The buttons lie over the row's right end; its name fades out before them
// (`.recent-export-row` in index.css). An action button is 22px, 2px apart;
// a finished row's Delete sits past a 1px line with 4px either side.
const ACTION_WIDTH_PX = 22
const ACTION_GAP_PX = 2
const SEPARATOR_WIDTH_PX = 9
const TICK_MS = 1_000

const FORMAT_ICONS: Record<ExportFormat, ReactNode> = {
  csv: <CsvFileIcon size={14} />,
  xlsx: <ExcelFileIcon size={14} />,
  pdf: <PdfFileIcon size={14} />,
}

const countFormat = new Intl.NumberFormat()

function isReady(job: ExportJob): boolean {
  return job.status === 'done'
}

/** A new export: one being made, or one ready that the person hasn't seen. */
function isNew(job: ExportJob): boolean {
  return isExportActive(job) || (isReady(job) && !job.seen_at)
}

/** Whether a link fetched at `fetchedAt` still has time left for a drag or an open. */
function isFresh(link: ExportLinkResponse | undefined, fetchedAt: number): boolean {
  return link !== undefined && Date.now() - fetchedAt < EXPORT_LINK_FRESH_MS
}

/** The time now, ticking while the list is open, for "5m". */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

/** The modifier keys a selecting click or key press carried. */
interface SelectKeys {
  shiftKey: boolean
  ctrlKey: boolean
  metaKey: boolean
}

/**
 * The person's exports, still downloadable, waiting or running, or failed
 * lately, newest first: a table's or list's under the formats in its Export
 * menu (collapsed under its header unless one is new), or all of them in the
 * header's Exports. One line each: the file's type, its name, and how long
 * ago (or how far along), where a hovered row shows its buttons instead.
 * Finished ones are selected as files are: a click selects one, Ctrl or Cmd
 * adds or removes one, Shift a range. Pressing a finished row and dragging
 * takes it out of the browser as its file (Chromium), or the whole selection
 * (one zip for a folder, each file for a page's drop zone); Download on a
 * selected row saves every selected file into one folder. A PDF prints again
 * from its print button, and Send emails a file (every selected file, on a
 * selected row) through the Send dialog. An export still being started (chosen, the server
 * not yet answering: a print being built) shows first as a waiting row, and
 * unfolds the list: progress shows here, never on the format rows.
 */
export default function RecentExports({
  exports,
  starting = 0,
  printing = false,
  sharing = false,
  collapsible = true,
  divider = true,
  onDialog,
}: {
  exports: ExportJob[]
  /** Exports chosen in this menu that the server hasn't answered yet. */
  starting?: number
  /** One of them is a print, or a share. */
  printing?: boolean
  sharing?: boolean
  /** A header that folds the list: the Export menu's. The header's Exports is only the list. */
  collapsible?: boolean
  /** A line above, separating it from the Export menu's formats. */
  divider?: boolean
  /** A row's button opened a dialog (Send): the menu around the list closes. */
  onDialog?: () => void
}) {
  const { addToast } = useToast()
  const cancel = useCancelExport()
  const now = useNow()
  const [dragOut] = useState(canDragDownloads)
  const [hintDone, setHintDone] = useState(hasDraggedOut)
  // Folded each time the menu opens, unless an export is new: decided once
  // its rows are in, and kept (seeing them doesn't fold the list).
  // Unfolded by hand once, it stays unfolded in every Export menu.
  const unfolds = () => !collapsible || keepsRecentUnfolded() || exports.some(isNew)
  const [expanded, setExpanded] = useState<boolean | null>(() =>
    exports.length > 0 ? unfolds() : null
  )
  if (expanded === null && exports.length > 0) setExpanded(unfolds())
  // An export started with the menu open (a print) unfolds the list, to show it coming.
  if (starting > 0 && !expanded) setExpanded(true)
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set())
  const [anchor, setAnchor] = useState<string | null>(null)
  const shown = exports.slice(0, RECENT_EXPORTS_SHOWN)
  const ready = shown.filter(isReady)
  // Exports that finished being listed, or stopped being downloadable, drop out.
  const selected = ready.filter((job) => chosen.has(job.export_id))
  const selectedIds = selected.map((job) => job.export_id)
  const several = selected.length > 1

  const bundle = useQuery({
    queryKey: exportsQueryKeys.bundle(selectedIds),
    queryFn: ({ signal }) => createExportBundle(selectedIds, signal),
    enabled: dragOut && several && Boolean(expanded),
    staleTime: EXPORT_LINK_FRESH_MS,
    refetchInterval: EXPORT_LINK_FRESH_MS,
    retry: false,
  })
  if (shown.length === 0 && starting === 0) return null

  const run = (action: () => Promise<void>, failure: string) =>
    action().catch((error: unknown) =>
      addToast({ type: 'error', title: failure, message: getErrorMessage(error) })
    )

  const select = (job: ExportJob, event: SelectKeys) => {
    const id = job.export_id
    if (event.shiftKey && anchor && ready.some((other) => other.export_id === anchor)) {
      const from = ready.findIndex((other) => other.export_id === anchor)
      const to = ready.findIndex((other) => other.export_id === id)
      const range = ready
        .slice(Math.min(from, to), Math.max(from, to) + 1)
        .map((other) => other.export_id)
      setChosen((current) =>
        event.ctrlKey || event.metaKey ? new Set([...current, ...range]) : new Set(range)
      )
      return
    }
    setAnchor(id)
    if (event.ctrlKey || event.metaKey) {
      // Ctrl or Cmd adds a row to the selection or takes it out.
      setChosen((current) => {
        const next = new Set(current)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
      return
    }
    // A plain click selects just this row, clearing the others; on the only
    // selected row, it clears it.
    setChosen((current) => (current.size === 1 && current.has(id) ? new Set() : new Set([id])))
  }

  const draggedOut = () => {
    if (hintDone) return
    markDraggedOut()
    setHintDone(true)
  }

  const bundleLink = isFresh(bundle.data, bundle.dataUpdatedAt) ? bundle.data : undefined
  const bundleReason = bundle.isError
    ? "These exports couldn't be zipped. Select them again in a moment."
    : `Preparing a zip of ${countFormat.format(selected.length)} exports`

  return (
    <>
      {divider && <PopoverMenuDivider />}
      {collapsible ? (
        <button
          type="button"
          aria-expanded={Boolean(expanded)}
          onClick={() => {
            const next = !expanded
            setExpanded(next)
            rememberRecentUnfolded(next)
          }}
          className={clsx(
            ROW_LAYOUT_CLASS,
            // As tall as a row (h-7), so the header and its rows read as one list.
            'h-7 text-left text-xs font-medium text-muted transition-colors hover:bg-[color:var(--bg-muted)] hover:text-subtle'
          )}
        >
          <span className={POPOVER_MENU_ICON_SLOT_CLASS}>
            {/* Smaller than the title, so the title leads. */}
            <ChevronRightIcon
              size={10}
              aria-hidden="true"
              className={clsx('transition-transform', expanded ? 'rotate-90' : 'rotate-0')}
            />
          </span>
          Recent Exports
        </button>
      ) : (
        // No chevron to make room for: the title starts where the rows' icons do.
        <p className={clsx(ROW_LAYOUT_CLASS, 'h-7 text-xs font-medium text-muted')}>
          Recent Exports
        </p>
      )}
      {expanded && (
        <>
          <ul className="flex max-h-72 flex-col overflow-y-auto">
            {Array.from({ length: starting }, (_, index) => (
              <li key={`starting-${index}`} className={clsx(ROW_LAYOUT_CLASS, 'h-7')}>
                <span
                  className={clsx(POPOVER_MENU_ICON_SLOT_CLASS, 'text-muted')}
                  role="img"
                  aria-label="Exporting"
                >
                  <SpinnerIcon size={12} />
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-muted">
                  {printing
                    ? 'Preparing to print…'
                    : sharing
                      ? 'Preparing to share…'
                      : 'Starting export…'}
                </span>
              </li>
            ))}
            {shown.map((job) => {
              const inSelection = several && chosen.has(job.export_id) && isReady(job)
              return (
                <RecentExportRow
                  key={job.export_id}
                  job={job}
                  now={now}
                  dragOut={dragOut}
                  selected={isReady(job) && chosen.has(job.export_id)}
                  group={
                    inSelection
                      ? {
                          count: selected.length,
                          link: bundleLink,
                          waitReason: bundleReason,
                        }
                      : null
                  }
                  onSelect={(event) => select(job, event)}
                  onDraggedOut={draggedOut}
                  onOpen={(link) => void run(() => openExport(job, link), "Couldn't open")}
                  onPrint={() => {
                    // Safari prints from a tab, opened inside the click.
                    const tab = canPrintInPage() ? null : window.open('', '_blank')
                    void run(async () => {
                      try {
                        if (!(await printExport(job, { tab }))) {
                          throw new Error(
                            'Your browser would not print it here. Open it to print it.'
                          )
                        }
                      } catch (error) {
                        tab?.close()
                        throw error
                      }
                    }, "Couldn't print")
                  }}
                  onSend={() => {
                    useSendExportStore.getState().open(inSelection ? selected : [job])
                    onDialog?.()
                  }}
                  onDownload={() =>
                    void run(
                      () =>
                        inSelection
                          ? downloadExports(selected)
                          : downloadExport(job, { saveAs: true }),
                      "Couldn't download"
                    )
                  }
                  onCancel={(failure) => void run(() => cancel(job.export_id), failure)}
                />
              )
            })}
          </ul>
          {dragOut && !hintDone && ready.length > 0 && (
            <p className={clsx(ROW_LAYOUT_CLASS, 'pb-1.5 pt-1 text-xs text-muted')}>
              <span className={POPOVER_MENU_ICON_SLOT_CLASS} aria-hidden="true" />
              Drag a file to your desktop or a folder
            </p>
          )}
        </>
      )}
    </>
  )
}

/** A selected row that's part of a selection of several: they drag out as one zip. */
interface SelectionDrag {
  count: number
  /** The zip (a browser drags out one file). */
  link: ExportLinkResponse | undefined
  /** Why the selection can't be dragged yet. */
  waitReason: string
}

/**
 * One recent export, on one line. A finished one is selected by a click
 * anywhere but its buttons, opens on a double-click, and pressed and dragged
 * goes out to a folder or the desktop as its file, by Chromium's
 * `DownloadURL` (its short-lived link, read once the pointer or focus
 * reaches the row and kept fresh: a drag can't wait for one). Its buttons
 * show while it's hovered or focused, over its right end where its time was.
 */
function RecentExportRow({
  job,
  now,
  dragOut,
  selected,
  group,
  onSelect,
  onDraggedOut,
  onOpen,
  onPrint,
  onSend,
  onDownload,
  onCancel,
}: {
  job: ExportJob
  now: number
  dragOut: boolean
  selected: boolean
  group: SelectionDrag | null
  onSelect: (keys: SelectKeys) => void
  onDraggedOut: () => void
  onOpen: (link: ExportLinkResponse | undefined) => void
  onPrint: () => void
  onSend: () => void
  onDownload: () => void
  onCancel: (failure: string) => void
}) {
  const active = isExportActive(job)
  const ready = isReady(job)
  const draggable = dragOut && ready
  const [wanted, setWanted] = useState(false)
  // Open uses the link too: a file opens inside the click, with no wait.
  const link = useQuery({
    queryKey: exportsQueryKeys.link(job.export_id),
    queryFn: ({ signal }) => getExportLink(job.export_id, signal),
    enabled: ready && wanted,
    staleTime: EXPORT_LINK_FRESH_MS,
    refetchInterval: EXPORT_LINK_FRESH_MS,
    retry: false,
  })
  const want = () => {
    if (ready) setWanted(true)
  }
  const freshLink = isFresh(link.data, link.dataUpdatedAt) ? link.data : undefined

  const onDragStart = (event: DragEvent<HTMLLIElement>) => {
    const url = group
      ? group.link && downloadUrlData(EXPORT_BUNDLE_MIME_TYPE, group.link.file_name, group.link.url)
      : freshLink && downloadUrlData(EXPORT_MIME_TYPES[job.format], job.file_name, freshLink.url)
    if (!url) {
      // Its link isn't in hand yet: no drag this time.
      event.preventDefault()
      want()
      return
    }
    // Only the link: Explorer refuses a drag that also carries files a page
    // made, so adding them stopped drags to folders.
    event.dataTransfer.effectAllowed = 'copy'
    event.dataTransfer.setData('DownloadURL', url)
    onDraggedOut()
  }

  const fromActions = (target: EventTarget) =>
    target instanceof Element && target.closest('[data-row-actions]') !== null

  // Clicks select as they do in a file list, and nothing waits: a double-click
  // is just two clicks (Open opens). Pressing a row that isn't selected
  // selects just it right away, before the button comes up. A press on a
  // selected row changes nothing yet, so a selection can be dragged out whole;
  // its click, without a drag, keeps just that row (or clears the only
  // selected one). Ctrl, Cmd and Shift act on the click.
  const pressSelected = useRef(false)

  const handlePointerDown = (event: PointerEvent<HTMLLIElement>) => {
    pressSelected.current = false
    if (!ready || event.button !== 0 || fromActions(event.target)) return
    if (selected || event.shiftKey || event.ctrlKey || event.metaKey) return
    pressSelected.current = true
    onSelect(event)
  }

  const handleRowClick = (event: MouseEvent<HTMLLIElement>) => {
    if (!ready || fromActions(event.target)) return
    if (pressSelected.current) {
      pressSelected.current = false
      return
    }
    onSelect(event)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (!ready || event.target !== event.currentTarget) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onSelect(event)
  }

  const finishedAt = job.finished_at ?? job.created_at
  const expires = ready ? expiresSoon(job.expires_at, now) : null
  const failed = job.status === 'failed'
  const about = ready
    ? `${EXPORT_FORMAT_LABELS[job.format]}, exported ${fullTimestamp(finishedAt)}${expires ? `. ${expires}` : ''}`
    : failed
      ? `Failed${job.error ? `: ${job.error}` : ''}`
      : null
  const waiting = group !== null && !group.link
  const printable = ready && job.format === 'pdf'
  // Print (a PDF's), Send, Save as… and Open, then a line and Delete; one
  // button (Cancel, or a failed row's Delete) otherwise.
  const actionCount = printable ? 5 : ready ? 4 : 1
  const separated = ready
  const actionsWidth =
    actionCount * ACTION_WIDTH_PX +
    (separated ? SEPARATOR_WIDTH_PX : 0) +
    (actionCount + (separated ? 1 : 0) - 1) * ACTION_GAP_PX

  return (
    <li
      tabIndex={0}
      draggable={draggable && !waiting}
      onDragStart={onDragStart}
      style={{ '--recent-export-actions-width': `${actionsWidth}px` } as CSSProperties}
      className={clsx(
        ROW_LAYOUT_CLASS,
        'recent-export-row group relative h-7 select-none outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-info',
        // Hover means clickable: only finished rows select. No color
        // transition, as in a file list: a row's fill changes the instant it's
        // selected, deselected or hovered.
        !ready
          ? undefined
          : selected
            ? 'cursor-pointer bg-[color:var(--sidebar-item-active-bg)] hover:bg-[color:var(--sidebar-item-active-hover-bg)]'
            : 'cursor-pointer hover:bg-[color:var(--bg-muted)]'
      )}
      data-selected={selected || undefined}
      onPointerEnter={want}
      onFocus={want}
      onPointerDown={handlePointerDown}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
    >
      <span
        className={clsx(POPOVER_MENU_ICON_SLOT_CLASS, failed ? 'text-danger' : 'text-muted')}
        role="img"
        aria-label={active ? 'Exporting' : EXPORT_FORMAT_LABELS[job.format]}
      >
        {active ? <SpinnerIcon size={12} /> : FORMAT_ICONS[job.format]}
      </span>
      <Tooltip
        variant="label"
        content={
          waiting
            ? group.waitReason
            : group
              ? `Drag ${countFormat.format(group.count)} exports out together`
              : (about ?? job.file_name)
        }
        // The name's box reaches the row's end, so its fade (less the buttons)
        // ends at the first button.
        fullWidth
        className="min-w-0 flex-1"
      >
        <span
          className={clsx(
            'recent-export-name block w-full truncate text-xs',
            selected ? 'text-[color:var(--sidebar-item-active-text)]' : 'text-subtle'
          )}
        >
          {job.file_name}
        </span>
      </Tooltip>
      <span
        className={clsx(
          // Keyboard focus shows the buttons as hover does; focus a click or an
          // opening menu put there (not :focus-visible) doesn't.
          'shrink-0 text-[11px] tabular-nums group-hover:hidden group-focus-visible:hidden group-has-[:focus-visible]:hidden',
          failed ? 'text-danger' : 'text-muted'
        )}
      >
        {ready ? (
          <time dateTime={finishedAt}>{relativeAge(finishedAt, now)}</time>
        ) : failed ? (
          'Failed'
        ) : (
          shortProgress(job)
        )}
      </span>
      <span
        data-row-actions=""
        // right-2: an icon sits 4px inside its button, so the icons end 12px in,
        // with the times, the format rows' sliders and the Data toggle.
        className="absolute inset-y-0 right-2 hidden items-center gap-0.5 group-hover:flex group-focus-visible:flex group-has-[:focus-visible]:flex"
      >
        {/* One order on every row (Print only where a PDF can print), so a
            button is always where it was; Delete is set apart past a line. */}
        {ready && (
          <>
            {printable && (
              <IconButton label="Print" className={ACTION_CLASS} onClick={onPrint}>
                <PrinterIcon size={14} />
              </IconButton>
            )}
            <IconButton
              label={group ? 'Send selected' : 'Send'}
              className={ACTION_CLASS}
              onClick={onSend}
            >
              <SendIcon size={14} />
            </IconButton>
            <IconButton
              // The file is already on the server (and often downloaded): where
              // the browser asks where to put it, this saves it there. The row
              // names the file, so the button says only what it does.
              label={
                group
                  ? canSaveToFolder()
                    ? 'Save to folder…'
                    : 'Download selected'
                  : canSaveAs()
                    ? 'Save as…'
                    : 'Download'
              }
              className={ACTION_CLASS}
              onClick={onDownload}
            >
              <DownloadIcon size={14} />
            </IconButton>
            <IconButton
              label={openLabel(job)}
              className={ACTION_CLASS}
              onClick={() => onOpen(freshLink)}
            >
              <OpenInNewIcon size={14} />
            </IconButton>
            <span aria-hidden="true" className="mx-1 h-4 w-px bg-[color:var(--border-default)]" />
          </>
        )}
        {active ? (
          <IconButton
            label="Cancel export"
            className={ACTION_CLASS}
            onClick={() => onCancel("Couldn't cancel the export")}
          >
            <CloseIcon size={14} />
          </IconButton>
        ) : (
          <IconButton
            // It deletes the file too, not just the line.
            label="Delete export"
            className={DELETE_CLASS}
            onClick={() => onCancel("Couldn't delete the export")}
          >
            <TrashIcon size={14} />
          </IconButton>
        )}
      </span>
    </li>
  )
}
